import {
  getToolForElement,
  BaseAnnotationTool,
  throttle,
  getNewContext,
  draw,
  setShadow,
  drawHandles,
  drawCircle,
  drawLinkedTextBox,
  drawJoinedLines,
} from 'app/CornerstoneTools';
import ChainingToolBuilder from 'app/CornerstoneTools/ChainingToolBuilder';
import checkPointNearHandles from 'app/CornerstoneTools/checkPointNearHandles';
import { drawToolStepExplanations } from 'app/CornerstoneTools/utils/toolStepsExplanations';
import * as cornerstone from 'cornerstone-core';
// @ts-ignore
import * as cornerstoneMath from 'cornerstone-math';
import {
  toolStyle,
  getToolState,
  toolColors,
  AnnotationHandle,
  BaseToolConfiguration,
  ToolOptions,
  IBaseAnnotationTool,
} from 'cornerstone-tools';
import * as _ from 'lodash';
import { norbergOlssonCursor } from './Cursors';
import { offsetPixelPointInCanvas } from './pixelToCanvasUtils';
import { computeNorbergOlssonAngles } from 'app/utils/predictions/convertPredictionsToNorbergOlssonAnnotation';
import {
  EventWithCornerstoneDetail,
  NorbergOlssonAnnotation,
  Point,
} from 'app/CornerstoneTools/types';
import { IntlShape } from 'react-intl';

const TEXT_BOX_HANDLE_INITIAL_DATA: Partial<AnnotationHandle> = {
  active: false,
  hasMoved: false,
  movesIndependently: false,
  drawnIndependently: true,
  allowedOutsideImage: true,
  hasBoundingBox: true,
};
Object.freeze(TEXT_BOX_HANDLE_INITIAL_DATA);

interface NorbergOlssonToolConfiguration extends BaseToolConfiguration {
  drawHandles: boolean;
  drawHandlesOnHover: boolean;
  hideHandlesIfMoving: boolean;
  renderDashed: boolean;
  angleTextOffset: Point;
  dicomDataPrinterToolName: string;
  getIntl: () => IntlShape;
}

export default class NorbergOlssonTool
  extends BaseAnnotationTool
  implements
    IBaseAnnotationTool<NorbergOlssonAnnotation, ToolOptions, NorbergOlssonToolConfiguration>
{
  public toolBuilder: ChainingToolBuilder<NorbergOlssonAnnotation>;
  public addNewMeasurement: ChainingToolBuilder<NorbergOlssonAnnotation>['addNewMeasurement'];
  public pointNearTool: typeof checkPointNearHandles;
  protected preventNewMeasurement: boolean = false;
  protected throttledUpdateCachedStats: (
    image: cornerstone.Image,
    element: HTMLElement,
    data: NorbergOlssonAnnotation
  ) => void;

  constructor(props: Partial<{ configuration: Partial<NorbergOlssonToolConfiguration> }> = {}) {
    const defaultProps = {
      name: 'NorbergOlsson',
      supportedInteractionTypes: ['Mouse', 'Touch'] as const,
      configuration: {
        drawHandles: true,
        drawHandlesOnHover: false,
        hideHandlesIfMoving: false,
        renderDashed: false,
        angleTextOffset: { x: 10, y: -20 },
        dicomDataPrinterToolName: 'DicomDataPrinter',
        getIntl: () => ({} as IntlShape),
      } as NorbergOlssonToolConfiguration,
      svgCursor: norbergOlssonCursor,
    };

    super(props, defaultProps);

    this.preventNewMeasurement = false;

    this.toolBuilder = new ChainingToolBuilder({
      name: this.name,
      createNewMeasurement: this.createNewMeasurement,
      // Points name contains left and right but this lateralization is not guaranteed since users
      // can start drawing tool from right to left. Those names are kept to avoid version
      // problem between full precomputed and manual/precomputed version.
      handlesKeys: [
        'leftFemoralHeadRadius',
        'leftFemoralHeadCenter',
        'leftAcetabularRim',
        'rightFemoralHeadRadius',
        'rightFemoralHeadCenter',
        'rightAcetabularRim',
      ],
      maximumToolData: 1,
    });
    this.addNewMeasurement = this.toolBuilder.addNewMeasurement;
    this.pointNearTool = checkPointNearHandles;

    this.throttledUpdateCachedStats = throttle(this.updateCachedStats, 110);
  }

  get configuration(): NorbergOlssonToolConfiguration {
    return super.configuration as NorbergOlssonToolConfiguration;
  }

  createNewMeasurement = (
    eventData: EventWithCornerstoneDetail['detail']
  ): NorbergOlssonAnnotation => ({
    visible: true,
    active: true,
    color: undefined,
    invalidated: true,
    handles: {
      leftFemoralHeadRadius: {
        x: eventData.currentPoints.image.x,
        y: eventData.currentPoints.image.y,
        highlight: true,
        active: true,
      },
      leftFemoralHeadCenter: {
        x: eventData.currentPoints.image.x,
        y: eventData.currentPoints.image.y,
        highlight: true,
        active: false,
      },
      leftAcetabularRim: {
        x: eventData.currentPoints.image.x,
        y: eventData.currentPoints.image.y,
        highlight: true,
        active: false,
      },
      rightFemoralHeadRadius: {
        x: eventData.currentPoints.image.x,
        y: eventData.currentPoints.image.y,
        highlight: true,
        active: false,
      },
      rightFemoralHeadCenter: {
        x: eventData.currentPoints.image.x,
        y: eventData.currentPoints.image.y,
        highlight: true,
        active: false,
      },
      rightAcetabularRim: {
        x: eventData.currentPoints.image.x,
        y: eventData.currentPoints.image.y,
        highlight: true,
        active: false,
      },
      textBoxL: {
        active: false,
        hasMoved: false,
        movesIndependently: false,
        drawnIndependently: true,
        allowedOutsideImage: true,
        hasBoundingBox: true,
        x: 0,
        y: 0,
      },
      textBoxR: {
        active: false,
        hasMoved: false,
        movesIndependently: false,
        drawnIndependently: true,
        allowedOutsideImage: true,
        hasBoundingBox: true,
        x: 0,
        y: 0,
      },
    },
  });

  updateCachedStats = (
    _image: cornerstone.Image,
    _element: HTMLElement,
    data: NorbergOlssonAnnotation
  ): void => {
    const { leftFemoralHeadCenter, leftAcetabularRim, rightFemoralHeadCenter, rightAcetabularRim } =
      data.handles;

    const { leftAngle, rightAngle, isAngleCorrect } = computeNorbergOlssonAngles(
      leftFemoralHeadCenter,
      leftAcetabularRim,
      rightFemoralHeadCenter,
      rightAcetabularRim
    );
    _.merge(data, { leftAngle, rightAngle, isAngleCorrect, invalidated: false });
  };

  drawAnnotations = (
    element: HTMLElement,
    context: CanvasRenderingContext2D,
    toolData: NorbergOlssonAnnotation
  ): void => {
    const {
      leftFemoralHeadCenter,
      leftFemoralHeadRadius,
      leftAcetabularRim,
      rightFemoralHeadCenter,
      rightFemoralHeadRadius,
      rightAcetabularRim,
    } = toolData.handles;

    const { handleRadius, drawHandlesOnHover, hideHandlesIfMoving } = this.configuration;
    setShadow(context, this.configuration);

    // Differentiate the color of activation tool
    const color = toolColors.getColorIfActive(toolData);

    const lineOptions = { color };
    const circleOptions = { color };

    const getCanvasDist = (p1: Point, p2: Point): number =>
      cornerstoneMath.point.distance(
        cornerstone.pixelToCanvas(element, p1 as cornerstone.PixelCoordinate),
        cornerstone.pixelToCanvas(element, p2 as cornerstone.PixelCoordinate)
      );

    if (leftFemoralHeadCenter && leftFemoralHeadRadius) {
      // Draw Circle
      drawCircle(
        context,
        element,
        leftFemoralHeadCenter,
        getCanvasDist(leftFemoralHeadCenter, leftFemoralHeadRadius),
        circleOptions,
        'pixel'
      );
    }
    if (rightFemoralHeadCenter && rightFemoralHeadRadius) {
      drawCircle(
        context,
        element,
        rightFemoralHeadCenter,
        getCanvasDist(rightFemoralHeadCenter, rightFemoralHeadRadius),
        circleOptions,
        'pixel'
      );
    }

    if (leftAcetabularRim) {
      drawJoinedLines(
        context,
        element,
        leftAcetabularRim,
        _.filter([leftFemoralHeadCenter, rightFemoralHeadCenter, rightAcetabularRim]),
        lineOptions
      );
    }

    // Draw the handles
    const handleOptions = {
      color,
      handleRadius,
      drawHandlesIfActive: drawHandlesOnHover,
      hideHandlesIfMoving,
    };

    if (this.configuration.drawHandles) {
      drawHandles(context, { element }, toolData.handles, handleOptions);
    }
  };

  drawTool = (
    element: HTMLElement,
    context: CanvasRenderingContext2D,
    toolData: NorbergOlssonAnnotation
  ): void => {
    this.drawAnnotations(element, context, toolData);
    this.updateCachedStats(undefined, undefined, toolData);
    this.drawToolTextBox(element, toolData, context);
  };

  drawToolTextBox = (
    element: HTMLElement,
    toolData: NorbergOlssonAnnotation,
    context: CanvasRenderingContext2D
  ): void => {
    const lineWidth = toolStyle.getToolWidth();
    const color = toolColors.getColorIfActive(toolData);
    const { angleTextOffset } = this.configuration;

    const displayTextBox = (
      angle: number | undefined,
      textBoxHandleKey: string,
      femoralHeadCHandle: Point,
      textAlign: CanvasTextAlign
    ): void => {
      if (!angle) return;

      if (toolData.handles[textBoxHandleKey] === undefined) {
        toolData.handles[textBoxHandleKey] = _.clone(
          TEXT_BOX_HANDLE_INITIAL_DATA
        ) as AnnotationHandle;
      }
      const textBoxHandle = toolData.handles[textBoxHandleKey];

      if (!textBoxHandle.hasMoved) {
        const pixelCoords = offsetPixelPointInCanvas(element, femoralHeadCHandle, {
          x: textAlign === 'left' ? angleTextOffset.x : -angleTextOffset.x,
          y: angleTextOffset.y,
        });
        textBoxHandle.x = pixelCoords.x;
        textBoxHandle.y = pixelCoords.y;
      }
      context.save();
      context.textAlign = textAlign;
      drawLinkedTextBox(
        context,
        element,
        textBoxHandle,
        `${angle}°`,
        toolData.handles,
        () => [femoralHeadCHandle],
        color,
        lineWidth,
        0,
        true
      );
      context.restore();
    };
    const { leftFemoralHeadCenter, rightFemoralHeadCenter } = toolData.handles;

    // In case hips are upside down left and right head may be wrong.
    // We use angle rotation direction to detect this change.
    const { leftAngle, rightAngle, isAngleCorrect } = toolData;
    const leftHead = isAngleCorrect ? leftFemoralHeadCenter : rightFemoralHeadCenter;
    const rightHead = isAngleCorrect ? rightFemoralHeadCenter : leftFemoralHeadCenter;
    displayTextBox(leftAngle, 'textBoxL', leftHead, isAngleCorrect ? 'left' : 'right');
    displayTextBox(rightAngle, 'textBoxR', rightHead, isAngleCorrect ? 'right' : 'left');
  };

  renderToolData = (evt: EventWithCornerstoneDetail): void => {
    const eventData = evt.detail;
    const { element } = eventData;

    // If we have no toolData for this element, return immediately as there is nothing to do
    const toolData = getToolState(evt.currentTarget, this.name);
    const { getIntl, dicomDataPrinterToolName } = this.configuration;

    // We have tool data for this element - iterate over each one and draw it
    const context = getNewContext(eventData.canvasContext.canvas);
    draw(context, (ctx: CanvasRenderingContext2D) => {
      const dicomDataPrinterTextOffset =
        getToolForElement(element, dicomDataPrinterToolName)?.dicomTextArea?.topLeft?.y ?? 0;

      drawToolStepExplanations(
        ctx,
        toolData?.data,
        getToolForElement(element, this.name).mode === 'active',
        [
          'tools.norberg_olsson.step1',
          'tools.norberg_olsson.step2',
          'tools.norberg_olsson.step3',
        ].map((id) => () => getIntl()?.formatMessage({ id })),
        [
          ['leftFemoralHeadRadius', 'rightFemoralHeadRadius'],
          ['leftFemoralHeadCenter', 'rightFemoralHeadCenter'],
          ['leftAcetabularRim', 'rightAcetabularRim'],
        ],
        {
          x: 5,
          y: dicomDataPrinterTextOffset + 20,
        },
        { maxWidth: (2 * eventData.canvasContext.canvas.width) / 5 }
      );
      toolData?.data?.forEach((data: NorbergOlssonAnnotation) => {
        if (this.toolBuilder.checkToolCreationCancelled(element, data)) return;
        this.drawTool(element, ctx, data);
      });
    });
  };
}
